import { isRecord } from "@arti/utilities";
import { useEffect, useState } from "react";

type ActorSystemEnabledState =
  | { status: "loading"; enabled: false }
  | { status: "ready"; enabled: boolean };

/**
 * Per-org actor-system enablement, backed by the DB org-flag system
 * (`actor_system` in the feature-flag registry → `organization_feature_overrides`,
 * toggleable from admin → Feature Flags). Replaces the legacy
 * `ARTI_ENABLE_ACTORS_UI` container-env client flag so enablement is per-org and
 * needs no redeploy. Reads the resolved flags from `/user/feature-flags`.
 */
export function useActorSystemEnabled(
  flagsPath = "/user/feature-flags",
): ActorSystemEnabledState {
  const [state, setState] = useState<ActorSystemEnabledState>({
    enabled: false,
    status: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const response = await fetch(flagsPath, { credentials: "include" });
      if (cancelled) {
        return;
      }
      if (!response.ok) {
        setState({ enabled: false, status: "ready" });
        return;
      }
      const body: unknown = await response.json();
      if (!cancelled) {
        setState({
          enabled: isRecord(body) && body.actor_system === true,
          status: "ready",
        });
      }
    };
    void load().catch(() => {
      if (!cancelled) {
        setState({ enabled: false, status: "ready" });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [flagsPath]);

  return state;
}
